import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, TrendingUp } from 'lucide-react';
interface UrgencyBannerProps {
  hours: string;
  minutes: string;
  seconds: string;
  totalSeconds?: number;
}
export function UrgencyBanner({
  hours,
  minutes,
  seconds,
  totalSeconds = 21600
}: UrgencyBannerProps) {
  // Calculate progress: how much time is LEFT (decreases as timer goes down)
  const remainingSeconds = parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60 + parseInt(seconds, 10);
  const progressPercent = Math.max(0, Math.min(100, remainingSeconds / totalSeconds * 100));
  return <section className="px-3 md:px-5 pt-6 md:pt-10" data-id="element-300">
      <div className="max-w-[860px] mx-auto" data-id="element-301">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-danger via-[#9E1F12] to-[#7A1407] border-2 border-gold/30 shadow-xl" data-id="element-302">
          {/* Subtle animated glow */}
          <motion.div animate={{
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 bg-gradient-to-r from-gold/0 via-gold/10 to-gold/0 pointer-events-none" data-id="element-303" />

          {/* Pulsing red dot top-left */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5 z-10" data-id="element-304">
            <motion.div animate={{
            scale: [1, 1.4, 1],
            opacity: [1, 0.5, 1]
          }} transition={{
            duration: 1.2,
            repeat: Infinity
          }} className="w-2 h-2 bg-gold rounded-full" data-id="element-305" />
            <span className="text-[9px] md:text-[10px] font-extrabold text-gold tracking-[0.15em] uppercase" data-id="element-306">
              EN VIVO
            </span>
          </div>

          <div className="relative px-4 py-4 md:px-7 md:py-6 text-white" data-id="element-307">
            {/* Title */}
            <div className="flex items-center justify-center gap-2 mb-2 md:mb-3 mt-3 md:mt-2" data-id="element-308">
              <AlertTriangle className="text-gold" size={18} fill="currentColor" data-id="element-309" />
              <h3 className="font-display text-[20px] md:text-[28px] tracking-wide text-center text-gold leading-none" data-id="element-310">
                ÚLTIMAS HORAS DE OFERTA
              </h3>
              <AlertTriangle className="text-gold" size={18} fill="currentColor" data-id="element-311" />
            </div>

            {/* Urgency message */}
            <p className="text-center text-[12px] md:text-[14px] text-white/90 max-w-[480px] mx-auto mb-3 md:mb-4 leading-snug" data-id="element-312">
              Cuando termine el contador, el precio sube{' '}
              <strong className="text-gold font-extrabold" data-id="element-313">$10.000</strong> y{' '}
              <strong className="text-white" data-id="element-314">no vuelve a bajar</strong>.
            </p>

            {/* Timer */}
            <div className="flex justify-center gap-1.5 md:gap-2.5 mb-3 md:mb-4" data-id="element-315">
              <TimerBlock value={hours} label="HRS" data-id="element-316" />
              <TimerSeparator data-id="element-317" />
              <TimerBlock value={minutes} label="MIN" data-id="element-318" />
              <TimerSeparator data-id="element-319" />
              <TimerBlock value={seconds} label="SEG" pulse data-id="element-320" />
            </div>

            {/* Progress bar */}
            <div className="max-w-[380px] mx-auto" data-id="element-321">
              <div className="flex items-center justify-between mb-1.5" data-id="element-322">
                <span className="text-[10px] md:text-[11px] font-bold text-white/70 uppercase tracking-wider flex items-center gap-1" data-id="element-323">
                  <TrendingUp size={11} data-id="element-324" /> Tiempo restante
                </span>
                <span className="text-[10px] md:text-[11px] font-extrabold text-gold" data-id="element-325">
                  {Math.round(progressPercent)}%
                </span>
              </div>
              <div className="relative bg-black/30 rounded-full h-2 md:h-2.5 overflow-hidden border border-white/10" data-id="element-326">
                <motion.div animate={{
                width: `${progressPercent}%`
              }} transition={{
                duration: 0.5
              }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-gold-light via-gold to-gold rounded-full" data-id="element-327" />
                {/* Shimmer effect */}
                <motion.div animate={{
                x: ['-100%', '300%']
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent" style={{
                width: `${progressPercent}%`,
                maxWidth: '120px'
              }} data-id="element-328" />
              </div>
              <p className="text-center text-[10px] md:text-[11px] text-white/60 mt-2" data-id="element-329">
                ⚡ Una vez agotado el tiempo, la oferta no se repite
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
function TimerBlock({
  value,
  label,
  pulse
}: {
  value: string;
  label: string;
  pulse?: boolean;
}) {
  const Wrapper = pulse ? motion.div : 'div';
  const animationProps = pulse ? {
    key: value,
    initial: {
      scale: 1,
      opacity: 1
    },
    animate: {
      scale: [1, 1.08, 1],
      opacity: [1, 0.85, 1]
    },
    transition: {
      duration: 0.3
    }
  } : {};
  return <Wrapper {...animationProps as any} className="bg-black/40 backdrop-blur-sm border border-gold/40 rounded-lg px-3 py-2 md:px-4 md:py-2.5 min-w-[52px] md:min-w-[64px] text-center" data-id="element-330">
      <div className="font-display text-[24px] md:text-[34px] text-gold leading-none tabular-nums" data-id="element-331">
        {value}
      </div>
      <div className="text-[8px] md:text-[9px] tracking-[0.12em] font-bold text-white/60 mt-0.5" data-id="element-332">
        {label}
      </div>
    </Wrapper>;
}
function TimerSeparator() {
  return <div className="flex items-center font-display text-[20px] md:text-[28px] text-gold/60" data-id="element-333">
      :
    </div>;
}